import { Requisition, RequisitionItem, PaymentOrder, DeliveryNote } from '../types';

const itemsOf = (req: Requisition): RequisitionItem[] =>
  (Array.isArray(req?.items) ? req.items : []);

export interface ReqItemLink {
  requisitionId: string;
  itemId: string;
}

/** Requisition items referenced by an auto-created payment order */
export function linksFromPaymentOrder(order: PaymentOrder): ReqItemLink[] {
  return (order.items || [])
    .filter((i: any) => i.requisitionId && i.requisitionItemId)
    .map((i: any) => ({ requisitionId: String(i.requisitionId), itemId: String(i.requisitionItemId) }));
}

/** Requisition items referenced by an auto-created delivery note */
export function linksFromDeliveryNote(note: DeliveryNote): ReqItemLink[] {
  if (!note.requisitionId) return [];
  return (note.technicalPart || [])
    .filter((i: any) => i.requisitionItemId || i.id)
    .map((i: any) => ({ requisitionId: String(note.requisitionId), itemId: String(i.requisitionItemId || i.id) }));
}

/**
 * Set a new status on the linked requisition items and persist each affected
 * requisition. `updateExec` is the execute fn of useUpdateRequisition.
 */
export async function syncRequisitionItemStatus(
  requisitions: Requisition[],
  updateExec: (args: { id: number; data: any }) => Promise<any>,
  links: ReqItemLink[],
  newStatus: RequisitionItem['status'],
  extras: Partial<RequisitionItem> = {}
): Promise<number> {
  let updated = 0;
  const byReq = new Map<string, Set<string>>();
  links.forEach((l) => {
    if (!byReq.has(l.requisitionId)) byReq.set(l.requisitionId, new Set());
    byReq.get(l.requisitionId)!.add(l.itemId);
  });

  const entries = Array.from(byReq.entries());
  for (const [reqId, itemIds] of entries) {
    const req = requisitions.find((r) => r.documentId === reqId || String(r.id) === reqId);
    if (!req) continue;
    const items = itemsOf(req).map((i) =>
      itemIds.has(String(i.id)) ? { ...i, status: newStatus, ...extras } : i
    );
    await updateExec({ id: parseInt(req.id), data: { ...req, documentId: req.documentId, items } });
    updated += itemIds.size;
  }
  return updated;
}
